import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Chip,
  CircularProgress,
  MenuItem,
  Box,
  Pagination,
  Alert,
  Snackbar
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddTaskIcon from '@mui/icons-material/AddTask';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingIcon from '@mui/icons-material/Pending';
import { motion } from 'framer-motion';
import { httpClient } from "../../axios";
import { useLocation } from "react-router-dom";
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from "react-router-dom";

// Enhanced Styling
const GradientBackground = styled(Container)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  minHeight: '100vh',
  padding: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
}));



const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
  background: 'rgba(255,255,255,0.9)',
  backdropFilter: 'blur(10px)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #6a11cb 0%, #2575fc 100%)',
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  textTransform: 'none',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 7px 14px rgba(50,50,93,0.1), 0 3px 6px rgba(0,0,0,0.08)',
  },
}));

const TaskChip = styled(Chip)(({ theme }) => ({
  fontWeight: 'bold',
  borderRadius: theme.spacing(1.5),
}));

const TaskPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  background: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(5px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  },
}));

const EmpTask = () => {
  const [user, setUser] = useState({
    taskName: "",
    startDate: "",
    endDate: "",
    taskStatus: "",
    taskPendingReason: "",
    taskPriority: "",
    employeeId: "",
  });
  const [errors, setErrors] = useState({});
  const [userTasks, setUserTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const tasksPerPage = 5;

  const location = useLocation();
  const navigate = useNavigate();

  const { userId, userName } = location.state || {};
  const handleLogout = () => {
    // Clear any authentication tokens or user data from local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Redirect to login page
    navigate('/login', { replace: true });
  };
  const toggleOrder = () => {
    setIsReversed(!isReversed);
  };
  const fetchUserTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await httpClient.get(`/api/employees/filter?name=${userName}`);
      setUserTasks(response.data[0]?.tasks || []);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUserTasks([]);
      setSnackbarMessage("Failed to fetch tasks");
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [userName]);

  useEffect(() => {
    if (userId && userName) {
      setUser(prevUser => ({
        ...prevUser,
        employeeId: userId,
      }));
      fetchUserTasks();
    }
  }, [userId, userName, fetchUserTasks]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser(prevUser => ({ ...prevUser, [name]: value }));
  };

  const handleRegister = async () => {
    const newErrors = {};
    Object.keys(user).forEach(key => {
      if (!user[key]) {
        newErrors[key] = `${key.charAt(0).toUpperCase() + key.slice(1)} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      await httpClient.post(`/task/add`, user);
      await fetchUserTasks();

      setUser({
        taskName: "",
        startDate: "",
        endDate: "",
        taskStatus: "",
        taskPendingReason: "",
        taskPriority: "",
        employeeId: userId || "",
      });
      setErrors({});
      setSnackbarMessage("Task added successfully!");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Error creating task:", error);
      setSnackbarMessage("Failed to add task. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = userTasks.slice(indexOfFirstTask, indexOfLastTask);
  const totalPages = Math.ceil(userTasks.length / tasksPerPage);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircleOutlineIcon color="success" />;
      case 'Pending': return <PendingIcon color="warning" />;
      default: return null;
    }
  };

  return (



    <GradientBackground maxWidth={false}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Grid container spacing={4}>
            {/* Task Creation Section */}
            <Grid item xs={12} md={5}>
              <StyledCard elevation={5}>
                <CardHeader
                  avatar={<AddTaskIcon sx={{ color: '#6a11cb', fontSize: 40 }} />}
                  title="Create New Task"
                  titleTypographyProps={{
                    variant: 'h6',
                    sx: {
                      color: '#6a11cb',
                      fontWeight: 'bold',
                      fontSize: '1.5rem'
                    }
                  }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Task Name"
                        name="taskName"
                        variant="outlined"
                        value={user.taskName}
                        onChange={handleChange}
                        error={!!errors.taskName}
                        helperText={errors.taskName}
                        disabled={isSubmitting}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        name="startDate"
                        type="date"
                        variant="outlined"
                        value={user.startDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                        disabled={isSubmitting}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        name="endDate"
                        type="date"
                        variant="outlined"
                        value={user.endDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                        disabled={isSubmitting}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Task Status</InputLabel>
                        <Select
                          name="taskStatus"
                          value={user.taskStatus}
                          onChange={handleChange}
                          label="Task Status"
                          error={!!errors.taskStatus}
                          disabled={isSubmitting}
                        >
                          <MenuItem value="Pending">Pending</MenuItem>
                          <MenuItem value="Completed">Completed</MenuItem>
                          <MenuItem value="Not Yet Started">Not Yet Started</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Task Priority</InputLabel>
                        <Select
                          name="taskPriority"
                          value={user.taskPriority}
                          onChange={handleChange}
                          label="Task Priority"
                          error={!!errors.taskPriority}
                          disabled={isSubmitting}
                        >
                          <MenuItem value="High">High</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="Low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Task Pending Reason"
                        name="taskPendingReason"
                        variant="outlined"
                        value={user.taskPendingReason}
                        onChange={handleChange}
                        error={!!errors.taskPendingReason}
                        helperText={errors.taskPendingReason}
                        disabled={isSubmitting}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <AnimatedButton
                        variant="contained"
                        fullWidth
                        onClick={handleRegister}
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                      >
                        {isSubmitting ? "Adding Task..." : "Add Task"}
                      </AnimatedButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Task List Section */}
            <Grid item xs={12} md={7}>
              <StyledCard elevation={5}>
                <CardHeader
                  avatar={<ListAltIcon sx={{ color: '#6a11cb', fontSize: 40 }} />}
                  title="My Tasks"
                  titleTypographyProps={{
                    variant: 'h6',
                    sx: {
                      color: '#6a11cb',
                      fontWeight: 'bold',
                      fontSize: '1.5rem'
                    }
                  }}
                  action={
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<LogoutIcon />}
                      onClick={handleLogout}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        borderRadius: 2
                      }}
                    >
                      Logout
                    </Button>
                  }
                />
                <CardContent>
                  {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                      <CircularProgress color="secondary" />
                    </Box>
                  ) : currentTasks.length === 0 ? (
                    <Typography variant="body1" color="textSecondary" align="center">
                      No tasks found. Create a new task!
                    </Typography>
                  ) : (
                    <>
                      {currentTasks.map((task) => (
                        <motion.div
                          key={task.taskId}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <TaskPaper elevation={3}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={1}>
                                {getStatusIcon(task.taskStatus)}
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{task.taskName}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {task.startDate} - {task.endDate}
                                </Typography>
                              </Grid>
                              <Grid item xs={4} container justifyContent="flex-end">
                                <TaskChip
                                  label={task.taskPriority}
                                  color={task.taskPriority === 'High' ? 'error' : task.taskPriority === 'Medium' ? 'warning' : 'info'}
                                  sx={{ mr: 1 }}
                                />
                                <TaskChip
                                  label={task.taskStatus}
                                  color={task.taskStatus === 'Completed' ? 'success' : 'warning'}
                                />
                              </Grid>
                            </Grid>
                          </TaskPaper>
                        </motion.div>
                      ))}

                      <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(e, value) => setCurrentPage(value)}
                          color="secondary"
                          size="large"
                        />
                      </Box>
                    </>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </motion.div>

        {/* Snackbar for Notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarMessage.includes('Failed') ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </GradientBackground>
  );
};

export default EmpTask;